import { getAuthToken } from "@/authentication/authToken"
import { defineGlobals } from "vue-utils"
import type { ApiTypeOf } from "@/models"
import { sendHttpRequest } from "./httpHelper"

export enum RequestMethod {
	GET = "GET",
	POST = "POST",
	DELETE = "DELETE",
	PUT = "PUT",
}

export const httpRequest = async (
	url: string,
	method: RequestMethod,
	requestOptions?: RequestInit
): Promise<Response> => {
	const requestInit: RequestInit = {
		method,
		...requestOptions,
	}

	const authTokenResult = await getAuthToken()

	//If the user is not logged in (or getting the token fails), attempt the API call anyway
	//Best case scenario, it doesn't require authentication. Worst case scenario, the user gets an Unauthorized response
	if (authTokenResult.status === "success") {
		const headers = (requestInit.headers ?? {}) as Record<string, string>
		headers.Authorization = `Bearer ${authTokenResult.token}`
		requestInit.headers = headers
	}

	if (url.startsWith("/") && window.location.host === "localhost:3000") {
		url = `http://localhost:5003${url}`
	}

	return await sendHttpRequest(url, requestInit)
}

export const httpRequestJsonResponse = async <T>(
	url: string,
	method: RequestMethod,
	requestOptions?: RequestInit
): Promise<ApiTypeOf<T>> => {
	const result = await httpRequest(url, method, requestOptions)
	return (await result.json()) as ApiTypeOf<T>
}

export const httpJsonRequest = async (url: string, content: unknown, method: RequestMethod): Promise<Response> =>
	await httpRequest(url, method, {
		body: JSON.stringify(content),
		headers: {
			"content-type": "application/json",
		},
	})

export const httpJsonRequestJsonResponse = async <T>(
	url: string,
	content: unknown,
	method: RequestMethod
): Promise<ApiTypeOf<T>> => {
	const result = await httpJsonRequest(url, content, method)
	return (await result.json()) as ApiTypeOf<T>
}

const serialiseVal = <T>(value: T): string | number | boolean => {
	if (value === null) {
		return "null"
	}

	if (value instanceof Date) {
		return value.toISOString()
	}

	switch (typeof value) {
		case "string":
			return value as string
		case "boolean":
			return value as boolean
		case "number":
			return value as number
		case "bigint":
			return value.toString()
		case "symbol":
			return value.toString()
	}
	return JSON.stringify(value)
}

export const sanitiseQueryParams = <T extends object>(params: T): string =>
	Object.entries(params)
		.filter(([, value]) => value !== undefined)
		.map(([key, value]) => `${key}=${encodeURIComponent(serialiseVal(value))}`)
		.join("&")

defineGlobals({
	RequestMethod,
	httpRequest,
	httpJsonRequest,
	httpRequestJsonResponse,
	httpJsonRequestJsonResponse,
	sanitiseQueryParams,
})
